/**
 * Copyright (c) 2026 eBUCIS, LLC. All rights reserved.
 *
 * PlanAdherenceBanner - accountable-but-motivating dashboard banner comparing the student's
 * ACTUAL study time this week against their RECOMMENDED weekly hours. Three states:
 *   - behind:   accountable + motivating (never defeating), with a concrete catch-up bump
 *   - on_track: brief affirmation
 *   - ahead:    congratulatory
 * Includes a "where you stand" bar: actual vs target hours with a clear target marker.
 *
 * Rendered only when the student has a goal + plan (gated by the parent).
 */

import PropTypes from 'prop-types'
import { Clock, TrendingUp, Target } from 'lucide-react'

const formatHrs = (n) => {
  const rounded = Math.round((Number(n) || 0) * 10) / 10
  // Drop a trailing ".0" so "2.0" reads as "2".
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

const STATE_STYLES = {
  behind: {
    wrapper:
      'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800',
    iconWrap: 'bg-gradient-to-br from-amber-500 to-orange-600',
    bar: 'bg-gradient-to-r from-amber-500 to-orange-500',
    icon: Clock,
    heading: 'Letting the plan slip a little this week'
  },
  on_track: {
    wrapper:
      'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200 dark:border-emerald-800',
    iconWrap: 'bg-gradient-to-br from-emerald-500 to-green-600',
    bar: 'bg-gradient-to-r from-emerald-500 to-green-500',
    icon: Target,
    heading: 'Right on pace with your plan'
  },
  ahead: {
    wrapper:
      'bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-primary-200 dark:border-primary-800',
    iconWrap: 'bg-gradient-to-br from-primary-500 to-blue-600',
    bar: 'bg-gradient-to-r from-primary-500 to-blue-500',
    icon: TrendingUp,
    heading: 'Ahead of your plan. Nice work.'
  }
}

const buildMessage = (state, recommendedWeeklyHours, actualHours, suggestedWeeklyHours) => {
  const target = formatHrs(recommendedWeeklyHours)
  const actual = formatHrs(actualHours)
  const bump = formatHrs(suggestedWeeklyHours || recommendedWeeklyHours)
  if (state === 'behind') {
    return `You're a bit behind your ${target} hrs/wk target (${actual} hr so far). Bumping to ${bump} hrs this week gets you back on track.`
  }
  if (state === 'ahead') {
    return `You've put in ${actual} hr this week, past your ${target} hrs/wk target. Keep that momentum going.`
  }
  return `You're at ${actual} of your ${target} hrs/wk target. Steady practice like this is exactly how goals get reached.`
}

const PlanAdherenceBanner = ({ data, onStartPractice }) => {
  if (!data || !data.hasGoal || !data.hasPlan) return null

  const {
    state = 'on_track',
    recommendedWeeklyHours = 0,
    actualHours = 0,
    suggestedWeeklyHours = 0
  } = data

  const style = STATE_STYLES[state] || STATE_STYLES.on_track
  const Icon = style.icon

  // "Where you stand" bar. Scale so both the target marker and the actual fill are visible,
  // with a little headroom when the student is ahead.
  const scaleMax = Math.max(recommendedWeeklyHours, actualHours, 0.1) * 1.15
  const targetPct = Math.min(100, (recommendedWeeklyHours / scaleMax) * 100)
  const actualPct = Math.max(0, Math.min(100, (actualHours / scaleMax) * 100))

  return (
    <div className={`border rounded-lg p-4 ${style.wrapper}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2.5 rounded-xl shadow-md ${style.iconWrap}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {style.heading}
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            {buildMessage(state, recommendedWeeklyHours, actualHours, suggestedWeeklyHours)}
          </p>
        </div>
      </div>

      {/* Where you stand: actual vs target hours this week */}
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
          <span>
            This week:{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatHrs(actualHours)} hr
            </span>
          </span>
          <span>
            Target:{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatHrs(recommendedWeeklyHours)} hr
            </span>
          </span>
        </div>
        <div
          className="relative h-3 bg-white/70 dark:bg-gray-800 rounded-full overflow-visible"
          role="img"
          aria-label={`You have studied ${formatHrs(actualHours)} hours of your ${formatHrs(
            recommendedWeeklyHours
          )} hour weekly target.`}
        >
          {/* Actual-hours fill */}
          <div
            className={`absolute inset-y-0 left-0 rounded-full ${style.bar}`}
            style={{ width: `${actualPct}%` }}
          />
          {/* Target marker */}
          <div
            className="absolute inset-y-[-2px] w-0.5 bg-gray-500 dark:bg-gray-300"
            style={{ left: `${targetPct}%` }}
            aria-hidden="true"
          />
          {/* "You are here" marker */}
          <div
            className="absolute -top-1 w-2.5 h-2.5 rounded-full bg-white border-2 border-gray-700 dark:border-gray-200 shadow -translate-x-1/2"
            style={{ left: `${actualPct}%` }}
            aria-hidden="true"
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[11px] text-gray-500 dark:text-gray-400">You are here</span>
          {onStartPractice && (
            <button
              onClick={onStartPractice}
              className="text-xs font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              {state === 'behind' ? 'Practice now' : 'Keep going'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

PlanAdherenceBanner.propTypes = {
  data: PropTypes.shape({
    hasGoal: PropTypes.bool,
    hasPlan: PropTypes.bool,
    state: PropTypes.oneOf(['behind', 'on_track', 'ahead']),
    recommendedWeeklyHours: PropTypes.number,
    actualHours: PropTypes.number,
    deviationHours: PropTypes.number,
    suggestedWeeklyHours: PropTypes.number
  }),
  onStartPractice: PropTypes.func
}

export default PlanAdherenceBanner
